import type { CohortEventRecord, CohortEventType } from "../auth/api-client";

const EVENT_TYPE_ICON: Record<Exclude<CohortEventType, "student_submitted" | "quiz_submitted">, string> = {
  cohort_created: "/cohort-created.svg",
  cohort_concluded: "/cohort-concluded.svg",
  student_joined: "/student-joined.svg",
  student_removed: "/student-removed.svg",
  lesson_released: "/lesson-released.svg",
  lesson_closed: "/lesson-closed.svg",
  student_gave_up: "/student-gave-up.svg",
  join_code_issued: "/join-code-issued.svg",
  join_code_disabled: "/join-code-disabled.svg",
};

export function cohortEventIconSrc(event: CohortEventRecord): string {
  if (event.eventType === "student_submitted") {
    return event.detail === "passed"
      ? "/student-submitted-passed.svg"
      : "/student-submitted-incomplete.svg";
  }
  if (event.eventType === "quiz_submitted") {
    return quizScoreDetailIsPerfect(event.detail)
      ? "/student-submitted-passed.svg"
      : "/student-submitted-incomplete.svg";
  }
  return EVENT_TYPE_ICON[event.eventType] ?? "/general-information.svg";
}

function quizScoreDetailIsPerfect(detail: string | null): boolean {
  if (!detail) return false;
  const match = /^(\d+)\/(\d+)$/.exec(detail.trim());
  if (!match) return false;
  const correct = Number.parseInt(match[1] ?? "0", 10);
  const total = Number.parseInt(match[2] ?? "0", 10);
  return total > 0 && correct === total;
}

export const EVENT_TYPE_FILTER_OPTIONS: { value: CohortEventType; label: string }[] = [
  { value: "cohort_created", label: "Cohort created" },
  { value: "cohort_concluded", label: "Cohort concluded" },
  { value: "student_joined", label: "Student joined" },
  { value: "student_removed", label: "Student removed" },
  { value: "lesson_released", label: "Lesson released" },
  { value: "lesson_closed", label: "Lesson closed" },
  { value: "student_submitted", label: "Student submitted" },
  { value: "quiz_submitted", label: "Quiz submitted" },
  { value: "student_gave_up", label: "Student gave up" },
  { value: "join_code_issued", label: "Join code issued" },
  { value: "join_code_disabled", label: "Join code disabled" },
];

export function studentOptionLabel(email: string | null): string {
  if (!email) return "Unknown student";
  const at = email.indexOf("@");
  if (at > 0) return email.slice(0, at);
  return email;
}

export function cohortEventTypeLabel(eventType: CohortEventType): string {
  return EVENT_TYPE_FILTER_OPTIONS.find((o) => o.value === eventType)?.label ?? eventType;
}

export function formatCohortEventMessage(event: CohortEventRecord, showCohort: boolean): string {
  const cohort = showCohort ? ` (${event.cohortLabel})` : "";
  const student = studentOptionLabel(event.studentEmail);
  const lesson = event.lessonTitle ?? "a lesson";

  switch (event.eventType) {
    case "cohort_created":
      return `Cohort created${showCohort ? `: ${event.cohortLabel}` : ""}`;
    case "cohort_concluded":
      return `Cohort concluded${showCohort ? `: ${event.cohortLabel}` : ""}`;
    case "student_joined":
      return `${student} joined${cohort}`;
    case "student_removed":
      return `${student} removed from roster${cohort}`;
    case "lesson_released":
      return `Lesson released: ${lesson}${cohort}`;
    case "lesson_closed":
      return `Lesson closed: ${lesson}${cohort}`;
    case "student_submitted": {
      const outcome =
        event.detail === "passed"
          ? " (passed)"
          : event.detail === "failed"
            ? " (did not pass)"
            : "";
      return `${student} submitted ${lesson}${outcome}${cohort}`;
    }
    case "quiz_submitted": {
      const score = event.detail ? ` (${event.detail})` : "";
      return `${student} submitted quiz ${lesson}${score}${cohort}`;
    }
    case "student_gave_up":
      return `${student} gave up on ${lesson}${cohort}`;
    case "join_code_issued":
      return `Join code issued${cohort}`;
    case "join_code_disabled":
      return `Join code disabled${cohort}`;
    default:
      return `Activity${cohort}`;
  }
}

export function eventMatchesActivityFilters(
  event: CohortEventRecord,
  filters: {
    cohortId: string;
    eventType: string;
    studentUserId: string;
    lessonId: string;
  }
): boolean {
  if (filters.cohortId && event.cohortId !== filters.cohortId) return false;
  if (filters.eventType && event.eventType !== filters.eventType) return false;
  if (filters.studentUserId && event.studentUserId !== filters.studentUserId) return false;
  if (filters.lessonId && event.lessonId !== filters.lessonId) return false;
  return true;
}
